import { Component, Input, OnChanges, OnDestroy } from '@angular/core';
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { SelectPesquisavelComponent } from '../../../../../shared/components/select-pesquisavel/select-pesquisavel.component';
import { EstoqueLoteSerieInlineComponent } from '../../../../../shared/components/estoque-lote-serie-inline/estoque-lote-serie-inline.component';
import { AlertCardComponent } from '../../../../../shared/components/alert-card/alert-card.component';
import { LookupItemStruct } from '../../../../../shared/services/structs/pessoa/lookup-item.struct';
import { EstoqueSaldoProduto } from '../../../../../shared/services/models/produto/estoque-saldo-produto.model';
import { LocalizacaoEstoque } from '../../../../../shared/services/models/produto/localizacao-estoque.model';
import { Subscription } from 'rxjs';
import { NgxCurrencyDirective } from 'ngx-currency';
import { AbntPipe } from '../../../../../shared/pipes/abnt.pipe';
import { EmptyStateComponent } from '../../../../../shared/components/empty-state/empty-state.component';

@Component({
  selector: 'app-produto-estoque',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatButtonModule,
    MatSlideToggleModule,
    SelectPesquisavelComponent,
    EstoqueLoteSerieInlineComponent,
    AlertCardComponent,
    NgxCurrencyDirective,
    AbntPipe,
    EmptyStateComponent,
  ],
  templateUrl: './produto-estoque.component.html',
  styleUrl: './produto-estoque.component.css',
})
export class ProdutoEstoqueComponent implements OnChanges, OnDestroy {
  @Input({ required: true }) form!: FormGroup;
  @Input({ required: true }) isKit!: boolean;
  @Input({ required: true }) isVariacao!: boolean;
  @Input({ required: true }) isEditMode!: boolean;
  @Input() quantidadeMask!: any;
  @Input() integerMask!: any;
  @Input() isFracionada = true;
  @Input() set listaLocalizacaoEstoque(value: LocalizacaoEstoque[]) {
    this._listaLocalizacaoEstoque = value;
    this.listaLocalizacaoEstoqueNaoPadrao = value ? value.filter(l => !l.isPadrao) : [];
  }
  get listaLocalizacaoEstoque(): LocalizacaoEstoque[] {
    return this._listaLocalizacaoEstoque;
  }
  private _listaLocalizacaoEstoque: LocalizacaoEstoque[] = [];
  public listaLocalizacaoEstoqueNaoPadrao: LocalizacaoEstoque[] = [];
  private formComValidador: FormGroup | null = null;
  private loteSerieSubscriptions: Subscription[] = [];
  private readonly loteSerieValidator = (control: AbstractControl): ValidationErrors | null => {
    const controlaLotes = control.get('isControlaLotes')?.value === true;
    const controlaSeries = control.get('isControlaSeries')?.value === true;

    return controlaLotes && controlaSeries ? { controleLoteSerieMutuamenteExclusivo: true } : null;
  };

  @Input() set dadosEstoqueCarregados(dados: EstoqueSaldoProduto[]) {
    if (dados) {
      this.localizacoesEstoque.clear();
      dados.forEach(item => this.addLocalEstoqueRow(item));
    }
    this.checkDefaultStockLocation();
  }

  ngOnChanges(changes: any): void {
    if (changes.form) {
      this.configurarExclusividadeSeriesLotes();
    }

    if (changes.listaLocalizacaoEstoque || changes.isKit || changes.isVariacao) {
      if (!this.isKit && !this.isVariacao) {
        this.checkDefaultStockLocation();
      }
    }
  }

  constructor(private fb: FormBuilder) {}

  ngOnDestroy(): void {
    this.loteSerieSubscriptions.forEach(subscription => subscription.unsubscribe());

    if (this.formComValidador) {
      this.formComValidador.removeValidators(this.loteSerieValidator);
      this.formComValidador.updateValueAndValidity({ emitEvent: false });
    }
  }

  get localizacoesEstoque(): FormArray {
    return this.form.get('localizacoesEstoque') as FormArray;
  }

  getControl(item: any, controlName: string): FormControl {
    return item.get(controlName) as FormControl;
  }

  private configurarExclusividadeSeriesLotes(): void {
    if (!this.form || this.formComValidador === this.form) return;

    this.loteSerieSubscriptions.forEach(subscription => subscription.unsubscribe());
    this.loteSerieSubscriptions = [];

    if (this.formComValidador) {
      this.formComValidador.removeValidators(this.loteSerieValidator);
      this.formComValidador.updateValueAndValidity({ emitEvent: false });
    }

    this.formComValidador = this.form;
    this.form.addValidators(this.loteSerieValidator);

    const lotesControl = this.form.get('isControlaLotes');
    const seriesControl = this.form.get('isControlaSeries');

    if (lotesControl && seriesControl) {
      this.loteSerieSubscriptions.push(
        lotesControl.valueChanges.subscribe(value => {
          if (value === true && seriesControl.value === true) {
            seriesControl.setValue(false, { emitEvent: false });
          }
          this.atualizarEstadoControlesLoteSerie();
          this.limparRastreioIncompativel();
          this.form.updateValueAndValidity({ emitEvent: false });
        })
      );

      this.loteSerieSubscriptions.push(
        seriesControl.valueChanges.subscribe(value => {
          if (value === true && lotesControl.value === true) {
            lotesControl.setValue(false, { emitEvent: false });
          }
          this.atualizarEstadoControlesLoteSerie();
          this.limparRastreioIncompativel();
          this.form.updateValueAndValidity({ emitEvent: false });
        })
      );

      this.atualizarEstadoControlesLoteSerie();
    }

    this.form.updateValueAndValidity({ emitEvent: false });
  }

  private atualizarEstadoControlesLoteSerie(): void {
    const lotesControl = this.form.get('isControlaLotes');
    const seriesControl = this.form.get('isControlaSeries');

    if (!lotesControl || !seriesControl) return;

    const controlaLotes = lotesControl.value === true;
    const controlaSeries = seriesControl.value === true;

    if (controlaLotes) {
      if (seriesControl.value === true) {
        seriesControl.setValue(false, { emitEvent: false });
      }
      if (seriesControl.enabled) {
        seriesControl.disable({ emitEvent: false });
      }
      if (lotesControl.disabled) {
        lotesControl.enable({ emitEvent: false });
      }
      return;
    }

    if (controlaSeries) {
      if (lotesControl.value === true) {
        lotesControl.setValue(false, { emitEvent: false });
      }
      if (lotesControl.enabled) {
        lotesControl.disable({ emitEvent: false });
      }
      if (seriesControl.disabled) {
        seriesControl.enable({ emitEvent: false });
      }
      return;
    }

    if (lotesControl.disabled) {
      lotesControl.enable({ emitEvent: false });
    }
    if (seriesControl.disabled) {
      seriesControl.enable({ emitEvent: false });
    }
  }

  addLocalEstoqueRow(data?: any): void {
    const group = this.fb.group({
      idLocalizacaoEstoque: [
        { value: data?.idLocalizacaoEstoque || null, disabled: this.isLocalPadrao(data?.idLocalizacaoEstoque) },
        Validators.required,
      ],
      // Mapeando saldoFisico (Estoque Atual)
      saldoFisico: [
        {
          value: data?.saldoFisico ?? null,
          disabled: this.isEditMode,
        },
      ],
      // Mapeando saldoDisponivel (Estoque Disponível)
      saldoDisponivel: [
        {
          value: data?.saldoDisponivel ?? null,
          disabled: true,
        },
      ],
      estoqueMinimo: [data?.estoqueMinimo ?? null],
      estoqueMaximo: [data?.estoqueMaximo ?? null],
      // Campo identificador importante para o PUT/POST
      idEstoqueSaldoProduto: [data?.idEstoqueSaldoProduto || null],
      // Balanço inicial: lote/série informados por local (só inclusão) + estado do painel.
      lotes: [data?.lotes || []],
      series: [data?.series || []],
      loteSerieCompleto: [true],
    });
    this.localizacoesEstoque.push(group);
  }

  removeLocalEstoqueRow(index: number): void {
    this.localizacoesEstoque.removeAt(index);
  }

  // ── Balanço inicial: lote/série por local ──────────────────────────────────

  /**
   * Ao trocar/desmarcar lote/série, limpa os dados incompatíveis dos FormArrays de estoque
   * (locais do produto e estoque das variações). Se desmarcar ambos, limpa tudo.
   */
  private limparRastreioIncompativel(): void {
    const tipo = this.tipoRastreio;
    const limparArray = (arr: FormArray | null) => {
      arr?.controls.forEach(loc => {
        if (tipo !== 'lote') loc.get('lotes')?.setValue([], { emitEvent: false });
        if (tipo !== 'serie') loc.get('series')?.setValue([], { emitEvent: false });
        loc.get('loteSerieCompleto')?.setValue(true, { emitEvent: false });
      });
    };

    limparArray(this.localizacoesEstoque);

    const combinacoes = this.form.get('combinacoes') as FormArray | null;
    combinacoes?.controls.forEach(comb => limparArray(comb.get('estoque') as FormArray | null));
  }

  /** Tipo de rastreio ativo no produto (lote OU série; exclusivos). */
  public get tipoRastreio(): 'lote' | 'serie' | null {
    if (this.form?.get('isControlaLotes')?.value === true) return 'lote';
    if (this.form?.get('isControlaSeries')?.value === true) return 'serie';
    return null;
  }

  /** Mostra o painel de lote/série da linha quando há rastreio e saldo informado (só inclusão). */
  public mostrarPainelRastreio(item: AbstractControl): boolean {
    return !this.isEditMode && this.tipoRastreio !== null && (Number(item.get('saldoFisico')?.value) || 0) > 0;
  }

  public itensRastreio(item: AbstractControl): any[] {
    const campo = this.tipoRastreio === 'serie' ? 'series' : 'lotes';
    return item.get(campo)?.value ?? [];
  }

  /** Códigos de série já usados em OUTROS locais deste produto — a série identifica uma
   * unidade física única, então não pode se repetir entre locais de estoque diferentes. */
  public codigosSerieEmOutrosLocais(itemAtual: AbstractControl): string[] {
    return this.localizacoesEstoque.controls
      .filter(c => c !== itemAtual)
      .flatMap(c => (c.get('series')?.value ?? []) as any[])
      .map(s => s.codigoSerie)
      .filter((c): c is string => !!c);
  }

  /** Nome do local (para dar contexto ao painel de lote/série). */
  public nomeLocal(item: AbstractControl): string {
    const id = item.get('idLocalizacaoEstoque')?.value;
    return this.listaLocalizacaoEstoque?.find(l => l.idLocalizacaoEstoque === id)?.nome ?? '';
  }

  public onItensRastreioChange(item: AbstractControl, itens: any[]): void {
    const campo = this.tipoRastreio === 'serie' ? 'series' : 'lotes';
    item.get(campo)?.setValue(itens);
  }

  public onCompletoChange(item: AbstractControl, completo: boolean): void {
    item.get('loteSerieCompleto')?.setValue(completo);
  }

  public linhaPendente(item: AbstractControl): boolean {
    return this.mostrarPainelRastreio(item) && item.get('loteSerieCompleto')?.value !== true;
  }

  /** Quantidade de locais com lote/série pendente (para o aviso no topo da seção). */
  public get qtdLocaisPendentes(): number {
    return this.localizacoesEstoque.controls.filter(c => this.linhaPendente(c)).length;
  }

  public isLocalPadrao(id: number): boolean {
    const item = this.listaLocalizacaoEstoque?.find(l => l.idLocalizacaoEstoque === id);
    return item?.isPadrao || false;
  }

  private checkDefaultStockLocation(): void {
    if (!this.listaLocalizacaoEstoque || this.listaLocalizacaoEstoque.length === 0) return;

    const defaultLocation = this.listaLocalizacaoEstoque.find(l => l.isPadrao);
    if (defaultLocation) {
      const exists = this.localizacoesEstoque.controls.some(
        ctrl => ctrl.get('idLocalizacaoEstoque')?.value === defaultLocation.idLocalizacaoEstoque
      );

      if (!exists) {
        this.addLocalEstoqueRow({
          idLocalizacaoEstoque: defaultLocation.idLocalizacaoEstoque,
          saldoFisico: 0,
          saldoDisponivel: 0,
          estoqueMinimo: 0,
          estoqueMaximo: 0,
        });
      }
    }
  }
}
